import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Plus, DollarSign } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useI18n } from '../contexts/I18nContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DealCard = ({ deal }) => {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: deal.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-shadow cursor-move"
    >
      <h4 className="font-semibold text-gray-900 mb-2">{deal.title}</h4>
      <div className="flex items-center gap-2 text-sm text-gray-600 mb-2">
        <DollarSign className="w-4 h-4" />
        <span className="font-medium">${deal.amount.toLocaleString()}</span>
      </div>
      {deal.probability > 0 && (
        <div className="text-xs text-gray-500">
          {deal.probability}% probability
        </div>
      )}
    </div>
  );
};

const Deals = () => {
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const { t } = useI18n();
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const STAGES = [
    { id: 'lead', title: t('deals.stages.lead', 'Lead'), color: 'bg-blue-100 border-blue-300' },
    { id: 'qualified', title: t('deals.stages.qualified', 'Qualified'), color: 'bg-purple-100 border-purple-300' },
    { id: 'proposal', title: t('deals.stages.proposal', 'Proposal'), color: 'bg-yellow-100 border-yellow-300' },
    { id: 'negotiation', title: t('deals.stages.negotiation', 'Negotiation'), color: 'bg-orange-100 border-orange-300' },
    { id: 'closed_won', title: t('deals.stages.closedWon', 'Closed Won'), color: 'bg-green-100 border-green-300' },
    { id: 'closed_lost', title: t('deals.stages.closedLost', 'Closed Lost'), color: 'bg-red-100 border-red-300' },
  ];
  const [formData, setFormData] = useState({
    title: '',
    amount: '',
    stage: 'lead',
    probability: 0,
    notes: '',
  });

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );

  useEffect(() => {
    fetchDeals();
  }, []);

  const importInputRef = React.useRef(null);
  const handleExportCsv = () => {
    window.location.href = `${API}/deals/export/csv`;
  };
  const handleImportClick = () => importInputRef.current?.click();
  const handleImportFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const form = new FormData();
    form.append('file', file);
    try {
      await axios.post(`${API}/deals/bulk/import`, form, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      fetchDeals();
    } catch (err) {
      console.error('Import failed', err);
    } finally {
      e.target.value = '';
    }
  };

  const fetchDeals = async () => {
    try {
      const response = await axios.get(`${API}/deals`, { withCredentials: true });
      setDeals(response.data);
    } catch (error) {
      console.error('Error fetching deals:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        amount: parseFloat(formData.amount),
        probability: parseInt(formData.probability),
      };
      await axios.post(`${API}/deals`, payload, { withCredentials: true });
      setIsDialogOpen(false);
      setFormData({
        title: '',
        amount: '',
        stage: 'lead',
        probability: 0,
        notes: '',
      });
      fetchDeals();
    } catch (error) {
      console.error('Error creating deal:', error);
    }
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;

    if (!over) return;

    const activeStage = active.data.current?.sortable?.containerId;
    const overStage = over.data.current?.sortable?.containerId || over.id;

    if (activeStage !== overStage) {
      const dealId = active.id;
      const deal = deals.find(d => d.id === dealId);

      if (deal) {
        try {
          await axios.put(
            `${API}/deals/${dealId}`,
            { ...deal, stage: overStage },
            { withCredentials: true }
          );
          fetchDeals();
        } catch (error) {
          console.error('Error updating deal stage:', error);
        }
      }
    }
  };

  const getDealsByStage = (stageId) => {
    return deals.filter(deal => deal.stage === stageId);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0176D3]"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">{t('pages.deals.title','Sales Pipeline')}</h1>
          <p className="text-gray-600 mt-2">{t('pages.deals.subtitle','Track and manage your deals')}</p>
        </div>

        <div className="flex gap-2 items-center">
          <input ref={importInputRef} type="file" accept=".csv" className="hidden" onChange={handleImportFile} />
          <Button variant="outline" onClick={handleImportClick}>Import CSV</Button>
          <Button variant="outline" onClick={handleExportCsv}>Export CSV</Button>
        </div>

        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-[#0176D3] hover:bg-[#0155A8]">
              <Plus className="w-4 h-4 mr-2" />
              {t('pages.deals.addBtn','Add Deal')}
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[500px]">
            <DialogHeader>
              <DialogTitle>Add New Deal</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="title">Deal Title *</Label>
                <Input
                  id="title"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label htmlFor="amount">Amount *</Label>
                <Input
                  id="amount"
                  type="number"
                  step="0.01"
                  value={formData.amount}
                  onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label htmlFor="stage">Stage</Label>
                <select
                  id="stage"
                  value={formData.stage}
                  onChange={(e) => setFormData({ ...formData, stage: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                >
                  {STAGES.map(stage => (
                    <option key={stage.id} value={stage.id}>{stage.title}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label htmlFor="probability">Probability (%)</Label>
                <Input
                  id="probability"
                  type="number"
                  min="0"
                  max="100"
                  value={formData.probability}
                  onChange={(e) => setFormData({ ...formData, probability: e.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="notes">Notes</Label>
                <textarea
                  id="notes"
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  rows="3"
                />
              </div>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" className="bg-[#0176D3] hover:bg-[#0155A8]">
                  Create Deal
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4">
          {STAGES.map((stage) => {
            const stageDeals = getDealsByStage(stage.id);
            const totalValue = stageDeals.reduce((sum, deal) => sum + deal.amount, 0);

            return (
              <Card key={stage.id} className={`${stage.color} border-2`}>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-semibold">
                    {stage.title}
                    <div className="text-xs font-normal text-gray-600 mt-1">
                      {stageDeals.length} deals · ${totalValue.toLocaleString()}
                    </div>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <SortableContext items={stageDeals.map(d => d.id)} strategy={verticalListSortingStrategy}>
                    {stageDeals.map((deal) => (
                      <DealCard key={deal.id} deal={deal} />
                    ))}
                  </SortableContext>
                  {stageDeals.length === 0 && (
                    <div className="text-center text-gray-400 text-sm py-8">
                      {t('pages.deals.noItems','No deals')}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      </DndContext>
    </div>
  );
};

export default Deals;